import { Location } from '@/api/types';
import { AircraftAvailabilityView, Employee } from '@/api/apis/resources';
import { DonorType, OrganType } from '@/api/apis/case';

export enum ActionTypeEnum {
  FlyAction = 'FlyAction',
  DriveAction = 'DriveAction',
  CommercialFlightAction = 'CommercialFlightAction',
  SwitchAction = 'SwitchAction',
  OperationAtHospitalAction = 'OperationAtHospitalAction',
  StayOvernightAction = 'StayOvernightAction',
  BlockAction = 'BlockAction',
}

export enum FlightTypeEnum {
  DIRECT = 'DIRECT',
  DIRECT_VIA_MIDDLE = 'DIRECT_VIA_MIDDLE',
  REPOSITION = 'REPOSITION',
  RETURN_TO_BASE = 'RETURN_TO_BASE',
}

export enum RouteTypeSettingsEnum {
  Full = 'FULL',
  Hub_DH = 'HUB_HOS',
  Custom_DH_TXC_Hub = 'CUS_HOS_TX_HUB',
  Custom_TXC_Hub = 'CUS_TX_HUB',
}

export enum TravelTypeHosTx {
  Default = 'default',
  Drive = 'drive',
  Flight = 'flight',
}

export enum StartLocationObjectType {
  Hub = 'Hub',
  DH = 'Hospital',
  TXC = 'TxCenter',
  Airport = 'Airport',
  Custom = 'PointLocation',
}

export enum PlanActionTeamKeyword {
  team = 'team',
  teamWithoutOcs = 'teamWithoutOcs',
  surgeonOnly = 'surgeonOnly',
}

export type TaskLocation = {
  id: string;
  name: string;
  code: string;
  location: Location;
  abstractLocationType: string;
  iata?: string;
  icao?: string;
  originalObjectId?: string;
  originalObjectType?: string;
};

export type RoutePlanAircraft = Pick<AircraftAvailabilityView, 'id' | 'registration'>;

export type PlanTeam = {
  ocs: {
    id: string;
  };
  specialist: Pick<Employee, 'id' | 'firstName' | 'lastName' | 'profilePhoto' | 'organSpecializations'>;
  surgeon: Pick<
    Employee,
    'id' | 'firstName' | 'lastName' | 'profilePhoto' | 'organSpecializations' | 'trackedLocation' | 'homeLocationObject'
  >;
};

export type TeamResourcesByKeyword = {
  [PlanActionTeamKeyword.team]: Pick<PlanTeam, 'specialist' | 'ocs'>;
  [PlanActionTeamKeyword.teamWithoutOcs]: Pick<PlanTeam, 'specialist'>;
  [PlanActionTeamKeyword.surgeonOnly]: Pick<PlanTeam, 'surgeon'>;
};

export type TaskActionCrewMember = {
  id: string;
  maoId: number;
  code: string;
  firstName: string;
  lastName: string;
  phone: string;
};

export type TaskAction = {
  id: string;
  actionType: ActionTypeEnum;
  flightType: FlightTypeEnum;
  subActions?: TaskAction[];
  from: TaskLocation;
  to: TaskLocation;
  startTime: string;
  endTime: string;
  duration: number;
  flightDuration: number;
  cost: number;
  teamKeyword?: PlanActionTeamKeyword;
  crew?: TaskActionCrewMember[];
  title?: string;
  description?: string;
  aircraftInfo?: RoutePlanAircraft;
  commercialAircraft?: RoutePlanAircraft;
  parentId?: string;
};

export type RoutePlanMetrics = {
  distanceBetweenHospitalAndHubMi: number;
  distanceBetweenTxCenterAndHubMi: number;
  driveActionsCount: number;
  totalAircraftsUsed: number;
  totalCost: number;
  totalDriveDuration: number;
  totalFlightDuration: number;
  totalFlightsCount: number;
  totalBlockDuration: number;
};

export type PlanType = {
  cost: number;
  actions: TaskAction[];
  planMetrics: RoutePlanMetrics;
  dryRunActions: TaskAction[];
  dryRunMetrics: RoutePlanMetrics;
} & Pick<TeamResourcesByKeyword, PlanActionTeamKeyword.team | PlanActionTeamKeyword.teamWithoutOcs>;

type RoutePlansType<T> = {
  errors: string[];
  warnings: string[];
  status: string;
  items: T;
};

export type PlanResponse = {
  benchmarkPlans: RoutePlansType<PlanType[]>;
  plans: RoutePlansType<PlanType[][]>;
  taskId: string;
};

export type PartialPlanResponse = {
  plans: RoutePlansType<PlanType[]>;
  taskId: string;
};

export type GenerateBenchmarkBody = {
  hospitalId: string;
  txCenterId: string;
  caseType: DonorType;
  organType: OrganType;
  orTime: string;
  caseId: string;
};

export type GenerateMultiOrPlanBody = {
  hospitalId: string;
  txCenterId: string;
  caseType: DonorType;
  organType: OrganType;
  orTimes: string[];
  caseId: string;
  isTrialProgram: boolean;
  partialTripType: RouteTypeSettingsEnum;
  forcedSpecialistId?: string;
  forcedHubId?: string;
  travelTypeHosTx?: TravelTypeHosTx;
  trialProgramId?: string;
  allowStandbyAtHosToOptimizeFlights: boolean;
  doRunOptimizations?: boolean;
};

export type StartSurgeonTripsGenerationBody = {
  forcedSurgeonId?: string;
};

export type PartialStartLocationType = {
  objectType: StartLocationObjectType;
  objectId: string;
  latitude: number;
  longitude: number;
};

export type GeneratePartialPlanBody = {
  partialTripType: RouteTypeSettingsEnum;
  hubId: string;
  txCenterId: string;
  organType: OrganType;
  tripStartTime: string;
  tripStartLocation: PartialStartLocationType;
  currentTime: string;
  caseId: string;
  allowStandbyAtHosToOptimizeFlights: boolean;
  hospitalId?: string;
  orTime?: string;
  travelTypeHosTx?: TravelTypeHosTx;
};

export type GetRefuelAirportsParams = {
  fromAirportMaoId: number;
  toAirportMaoId: number;
};

export type RefuelAirportsResponse = {
  airports: {
    maoId: number;
  }[];
};

export type RouteActionsType = {
  errors: string[];
  status: string;
  actions: TaskAction[];
};

export type SurgeonTrip = {
  surgeon: Pick<
    Employee,
    'id' | 'firstName' | 'lastName' | 'profilePhoto' | 'organSpecializations' | 'trackedLocation' | 'homeLocationObject'
  >;
  fromHome: RouteActionsType;
  toHome: RouteActionsType;
};

export type SurgeonTripsResponse = {
  status: string;
  surgeonTrips2D: SurgeonTrip[][];
  errors: string[];
  warnings: string[];
};

export type CommercialFlightsTripsResponse = {
  status: string;
  planCommercialFlights2D: RouteActionsType[][];
  errors: string[];
  warnings: string[];
};

export type PartialCommercialFlightsTripsResponse = {
  status: string;
  planCommercialFlights: RouteActionsType[];
  errors: string[];
  warnings: string[];
};
